import React, { useEffect, useMemo, useState } from "react";
import SubjectSettingTableRow from "./SubjectSettingTableRow";
import Button from "../../shared/Button";
import {
  useSubjectSettings,
  useRecruitmentStatus,
  useRecruitment,
} from "../../../hooks/useSubjects";
import swal from "sweetalert";
import { Spinner } from "reactstrap";

const SubjectSettings = ({ subject, closeModal, refetchSubjects }) => {
  const [settings, setSettings] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const { loading, subjectsSettings, createSubjectSettings } =
    useSubjectSettings({ subjectId: subject.id, refetchSubjects });
  const { recruitmentStatus } = useRecruitmentStatus();
  const { staffList, getStaffList } = useRecruitment();

  const handleChange = (id, e, termId, option, inputType) => {
    if (!id) return;

    if (inputType === "select") {
      setSettings((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;

          let current = { ...item };

          current.subjectCoordinatorRecruitmentId = option?.value ?? null;
          return current;
        }),
      );

      return;
    }

    const { name, value, checked, type } = e.target;
    setSettings((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        let current = { ...item };

        //handle terms
        if (termId) {
          current.educationLevelDivisions = item.educationLevelDivisions?.map(
            (level) => {
              if (level.educationLevelDivisionId !== termId) return level;

              let term = { ...level };

              if (name === level.name) {
                term.checked = level?.isRecorded || checked;
              } else if (name === "isCompulsory") {
                term.isCompulsory = level?.isRecorded || checked;
              }

              return term;
            },
          );
          return current;
        }

        //calculate total credit
        if (name === "creditHourTheory" || name === "creditHourPractical") {
          current[name] = value;
          current.creditHour =
            Number(current?.creditHourTheory ?? 0) +
            Number(current?.creditHourPractical ?? 0);
          return current;
        }

        current[name] =
          type === "checkbox" ? current?.isRecorded || checked : value;
        return current;
      }),
    );

    return;
  };

  const handleSubmit = () => {
    const hasInvalidCheckedClass = settings?.some(
      (cls) =>
        cls.checked &&
        !cls.educationLevelDivisions?.some((item) => item.checked),
    );

    if (hasInvalidCheckedClass) {
      swal("Oops", "Each selected class must have at least one term selected");
      return;
    }

    createSubjectSettings(settings);
  };

  const handleSelectAll = (e) => {
    debugger;
    const { checked } = e.target;

    setSelectAll(checked);

    setSettings((prev) => {
      const updated = prev?.map((item) => {
        const educationLevelDivisions = item?.educationLevelDivisions?.map(
          (division) => ({
            ...division,
            checked: checked || division?.isConfigured,
            isCompulsory: checked || division?.isConfigured,
          }),
        );

        //check whether some term are configured or not to allow check/uncheck
        const isConfigured = item?.educationLevelDivisions?.some(
          (item) => item?.isConfigured,
        );

        return {
          ...item,
          educationLevelDivisions,
          checked: isConfigured || checked,
        };
      });
      return updated;
    });
  };

  const staffListOptions = useMemo(() => {
    if (staffList?.length === 0) return;
    return staffList?.map((item) => ({
      label: item?.people?.name,
      value: item.id,
    }));
  }, [staffList, recruitmentStatus]);

  //checked select all input if all item is selected
  useEffect(() => {
    if (settings?.length === 0) {
      setSelectAll(false);
      return;
    }

    const isAllChecked = settings?.every((row) => {
      return row?.educationLevelDivisions?.every((item) => {
        return row?.checked && item?.checked && item?.isCompulsory;
      });
    });

    setSelectAll(isAllChecked);
  }, [settings]);

  //fetch staffs
  useEffect(() => {
    if (recruitmentStatus?.length > 0) {
      const recruitmentStatusId = recruitmentStatus.find(
        (item) => item?.code === "rt-cur",
      )?.id;
      getStaffList(recruitmentStatusId);
    }
  }, [recruitmentStatus]);

  //populate subject settings in local settings
  useEffect(() => {
    if (subjectsSettings.length > 0) {
      const newSettings = subjectsSettings?.map((item) => ({
        id: item?.yearLevelDto?.id,
        subjectId: item?.yearLevelDto?.subjectId,
        yearLevelId: item?.yearLevelDto?.yearLevelId,
        educationLevelId: item?.yearLevelDto?.educationLevelId,
        name: item?.yearLevelDto?.name,
        subjectCoordinatorRecruitmentId:
          item?.yearLevelDto?.subjectCoordinatorRecruitmentId ?? "",
        checked: item?.yearLevelDto?.checked,
        creditHour: item?.yearLevelDto?.creditHour ?? "",
        creditHourTheory: item?.yearLevelDto?.creditHourTheory ?? "",
        creditHourPractical: item?.yearLevelDto?.creditHourPractical ?? "",
        subjectCoordinatorRecruitmentId:
          item?.yearLevelDto?.subjectCoordinatorRecruitmentId,
        educationLevelDivisions: item?.educationLevelDivisionDtos?.map(
          (level) => ({
            educationLevelDivisionId: level?.id,
            name: level?.name,
            isCompulsory: level?.isCompulsory,
            checked: level?.doesExist,
            isConfigured: level?.isConfigured,
          }),
        ),
      }));

      setSettings(newSettings);
    }
  }, [subjectsSettings]);

  return (
    <>
      <div className="arr-table-container">
        <table aria-label="subject-settings-table" className="arr-table">
          <thead>
            <tr>
              <th>
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={selectAll}
                  name="checked"
                  onChange={handleSelectAll}
                />
              </th>
              <th>Class</th>
              {subject?.hasTheory && <th>Theory Credit Hour</th>}
              {subject?.hasPractical && <th>Practical Credit Hour</th>}
              <th>Total Credit Hour</th>
              <th>Term</th>
              <th>Coordinator</th>
            </tr>
          </thead>
          <tbody>
            {loading.read ? (
              <tr>
                <td colSpan={5}>
                  <div className="arr-spinner">
                    <div className="loader">
                      <Spinner size={"md"} color="primary"></Spinner>
                    </div>
                    <div style={{ fontSize: "13px", marginTop: "15px" }}>
                      Please wait !! Processing.....
                    </div>
                  </div>
                </td>
              </tr>
            ) : settings?.length > 0 ? (
              settings.map((item) => (
                <SubjectSettingTableRow
                  subject={subject}
                  key={item.id}
                  handleChange={handleChange}
                  data={item}
                  staffListOptions={staffListOptions}
                />
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="action-btns end">
        <Button onClick={closeModal} variant="outline">
          Cancel
        </Button>
        <Button disabled={loading.create} onClick={handleSubmit}>
          {loading.create ? <Spinner size={"sm"} color="white" /> : "Save"}
        </Button>
      </div>
    </>
  );
};

export default SubjectSettings;
